import React, { useState, useEffect } from 'react';
import './App.css';
import Papa from 'papaparse';
import { checkAuthToken } from './AuthApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Button, Table, Input, Space, Typography, Divider } from 'antd';
import { SearchOutlined, BarChartOutlined, FieldTimeOutlined, CheckCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { format, parseISO, subMonths, subYears, isAfter, getDay } from 'date-fns';

const { Text } = Typography;

/**
 * Helper function to parse dates from CSV files
 * Tries ISO format first, then falls back to more flexible format with current year
 */
const parseDate = (dateStr) => {
  // Try to parse as ISO format (YYYY-MM-DD)
  try {
    return parseISO(dateStr);
  } catch (error) {
    // Fallback for the old format (e.g., "Jan 6")
    const currentYear = new Date().getFullYear();
    return new Date(`${dateStr}, ${currentYear}`);
  }
};

/**
 * Custom tooltip component for the chart
 * Displays formatted date and percentage values for each data series
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Get the full date with day of week from the first payload item
    let fullDateDisplay = label;
    if (payload[0]?.payload?.date) {
      try {
        const date = parseDate(payload[0].payload.date);
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 480;
        // Use shorter date format on mobile
        fullDateDisplay = format(date, isMobile ? 'MM/dd/yy' : 'yyyy-MM-dd EEEE');
        
      } catch (error) {
        console.error('Error formatting date for tooltip:', error);
      }
    }

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 480;

    return (
      <div style={{ 
        backgroundColor: '#FFFFFF', 
        padding: isMobile ? '8px' : '10px', 
        border: '1px solid #E2E8F0',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        fontSize: isMobile ? '10px' : '12px',
        color: 'inherit',
        maxWidth: isMobile ? '150px' : 'none',
        wordWrap: 'break-word'
      }}>
        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{`Date: ${fullDateDisplay}`}</p>
        {payload.map((entry, index) => {
          // Format the value with highlighted negative numbers
          const formattedValue = entry.value !== null ? `${entry.value}%` : 'N/A';
          
          return (
            <p key={`item-${index}`} style={{ margin: '2px 0', color: entry.color }}>
              {`${entry.name}: `}{formattedValue}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

/**
 * Main component that displays portfolio performance data
 * Includes interactive chart and transactions table
 */
const VaultContent = () => {
  // State variables for data management and UI control
  const [tableData, setTableData] = useState([]);               // Holds transaction data for the table
  const [chartData, setChartData] = useState([]);               // Stores all chart data points
  const [filteredChartData, setFilteredChartData] = useState([]); // Filtered data based on time range
  const [timeFilter, setTimeFilter] = useState('all');           // Current time filter (1m, 3m, 6m, 1y, all)
  const [selectedRowKey, setSelectedRowKey] = useState(null);   // Selected row in transactions table
  const [selectedTicker, setSelectedTicker] = useState(null);   // Currently selected ticker
  const [tickerChartData, setTickerChartData] = useState([]);   // Performance data for selected ticker
  const [isTickerDataLoading, setIsTickerDataLoading] = useState(false); // Loading state
  // For responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Memoization for chart performance optimization
  const [memoizedChartData, setMemoizedChartData] = useState(null); // Cached chart data
  const [memoizationKey, setMemoizationKey] = useState('');         // Key for recomputation

  /**
   * Color and theme configuration
   * Provides consistent visual styling for the chart and UI elements
   */
  const seriesColors = {
    'Cogitat': '#2E5090', // Darker blue for portfolio
    'S&P 500': '#D64045',      // Darker red for S&P 500
    'Selected Ticker': '#10B981', // Green for selected ticker
  };

  /**
   * Format date for display on X-axis
   * Converts dates to a compact MM/dd format
   */
  const formatDateForAxis = (dateStr) => {
    try {
      const date = parseDate(dateStr);
      return format(date, 'MM/dd'); // Format as "01/15" for better readability
    } catch (error) {
      return dateStr; // Return original if parsing fails
    }
  };

  /**
   * Check if a date is a Saturday
   * Used to highlight weekend days on the chart
   */
  const isSaturdayDate = (dateStr) => {
    try {
      const date = parseDate(dateStr);
      return getDay(date) === 6; // 6 is Saturday in JavaScript's getDay() (0-6, where 0 is Sunday)
    } catch (error) {
      return false;
    }
  };

  /**
   * Filter data based on selected time period
   * Returns data points that fall within the selected timeframe
   */
  const filterDataByTime = (data, filter) => {
    if (filter === 'all' || !data || data.length === 0) {
      return data;
    }
    
    // Get the most recent date in the data
    const dates = data.map(item => parseDate(item.date));
    const mostRecentDate = new Date(Math.max(...dates));
    
    // Calculate the cutoff date based on the filter
    let cutoffDate;
    
    switch (filter) {
      case '1m':
        cutoffDate = subMonths(mostRecentDate, 1);
        break;
      case '3m':
        cutoffDate = subMonths(mostRecentDate, 3);
        break;
      case '6m':
        cutoffDate = subMonths(mostRecentDate, 6);
        break;
      case '1y':
        cutoffDate = subYears(mostRecentDate, 1);
        break;
      default:
        return data;
    }
    
    // Filter the data to include only dates after the cutoff
    return data.filter(item => {
      try {
        const itemDate = parseDate(item.date);
        return isAfter(itemDate, cutoffDate) || itemDate.getTime() === cutoffDate.getTime();
      } catch (e) {
        return false;
      }
    });
  };

  /**
   * Extract ticker-specific data from CSV text
   * Parses CSV to extract performance data for a specific ticker
   */
  const extractTickerData = (csvText, ticker) => {
    if (!ticker) {
      return { success: false, error: 'No ticker provided' };
    }
    
    const tickerLower = ticker.toLowerCase();
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    // Check if the ticker exists as a column in the CSV (case insensitive)
    let tickerColumnIndex = -1;
    let exactTickerName = ticker;  // Store the exact ticker name as it appears in the CSV
    
    for (let i = 0; i < headers.length; i++) {
      if (headers[i].toLowerCase() === tickerLower) {
        tickerColumnIndex = i;
        exactTickerName = headers[i];
        break;
      }
    }
    
    if (tickerColumnIndex === -1) {
      console.warn(`Ticker column "${ticker}" not found in CSV headers:`, headers);
      return { success: false, error: 'Ticker column not found' };
    }
    
    // Find the date column index
    const dateColumnIndex = headers.findIndex(h => h === 'Date');
    
    if (dateColumnIndex === -1) {
      console.warn('Date column not found in CSV headers:', headers);
      return { success: false, error: 'Date column not found' };
    }
    
    // Process data rows
    const processedData = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i] || lines[i].trim() === '') continue; // Skip empty lines
      
      const values = lines[i].split(',').map(v => v.trim());
      
      // Skip rows that don't have enough values
      if (values.length <= Math.max(dateColumnIndex, tickerColumnIndex)) {
        continue;
      }
      
      const rowDate = values[dateColumnIndex];
      let rowValue = null;
      
      // Try to parse the value as a number
      if (values[tickerColumnIndex] !== undefined && values[tickerColumnIndex] !== '') {
        rowValue = parseFloat(values[tickerColumnIndex]);
      }
      
      // Add data point if we have a valid date and value
      if (rowDate && rowValue !== null && !isNaN(rowValue)) {
        processedData.push({
          date: rowDate,
          formattedDate: formatDateForAxis(rowDate),
          tickerValue: rowValue,
          isSaturday: isSaturdayDate(rowDate)
        });
      }
    }
    
    if (processedData.length === 0) {
      console.warn(`No valid data points found for ticker: ${ticker}`);
      return { success: false, error: 'No valid data points found' };
    }
    
    // Sort the data by date
    processedData.sort((a, b) => {
      try {
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);
        return dateA - dateB;
      } catch (e) {
        return 0;
      }
    });
    
    return { success: true, data: processedData, tickerName: exactTickerName };
  };

  /**
   * Load ticker-specific performance data
   * Fetches and processes data for the selected ticker
   */
  const loadTickerData = (ticker) => {
    if (!ticker) {
      console.warn('No ticker provided to loadTickerData');
      return;
    }
    
    setIsTickerDataLoading(true);
    
    fetch('/data/return_over_time.csv')
      .then(response => response.text())
      .then(csvText => {
        const result = extractTickerData(csvText, ticker);
        
        if (!result.success) {
          console.warn(`Failed to extract data for ${ticker}: ${result.error}`);
          setTickerChartData([]);
          setSelectedTicker(null);
          setIsTickerDataLoading(false); // Ensure loading state is reset
          return;
        }
        
        // Use the exact ticker name as it appears in the CSV
        const exactTickerName = result.tickerName || ticker;
        
        // Apply the current time filter to the ticker data
        const filteredTickerData = filterDataByTime(result.data, timeFilter);
        
        setTickerChartData(filteredTickerData);
        setSelectedTicker(exactTickerName);
        
        // Reset memoized data
        resetMemoization();
      })
      .catch(error => {
        console.error('Error loading ticker data:', error);
        setTickerChartData([]);
        setSelectedTicker(null);
      })
      .finally(() => {
        setIsTickerDataLoading(false);
      });
  };

  /**
   * Reset memoization cache to force recalculation of chart data
   * Used when changing filters, selecting tickers, or toggling series
   */
  const resetMemoization = () => {
    setMemoizedChartData(null);
    setMemoizationKey('');
  };

  /**
   * Handle time filter change
   * Updates filtered data and reloads ticker data if needed
   */
  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
    
    // Filter the main chart data
    const filteredData = filterDataByTime(chartData, filter);
    setFilteredChartData(filteredData);
    
    // Also filter the ticker data if it exists
    if (selectedTicker && tickerChartData.length > 0) {
      // Get the original ticker data and filter it
      fetch('/data/return_over_time.csv')
        .then(response => response.text())
        .then(csvText => {
          const result = extractTickerData(csvText, selectedTicker);
          
          if (result.success) {
            // Apply the new time filter to the ticker data
            const filteredTickerData = filterDataByTime(result.data, filter);
            setTickerChartData(filteredTickerData);
          }
        })
        .catch(error => console.error('Error reloading ticker data:', error));
    }
    
    // Reset memoized data
    resetMemoization();
  };

  /**
   * Custom filter for AntD Table
   * Enables search functionality for table columns
   */
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            
          }}
          onPressEnter={() => {
            confirm();
            
          }}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Filter
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
  });

  /**
   * Column definitions for the transactions table
   * Includes sorting, filtering, and custom rendering
   */
  const columns = [
    {
      title: 'Ticker',
      dataIndex: 'Ticker',
      key: 'Ticker',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {selectedRowKey === record.key && (
            <CheckCircleFilled 
              style={{ 
                color: '#1890ff', 
                marginRight: windowWidth < 420 ? '4px' : '8px',
                fontSize: windowWidth < 420 ? '12px' : windowWidth < 480 ? '14px' : '16px'
              }} 
            />
          )}
          <span style={{ fontSize: windowWidth < 420 ? '9px' : undefined }}>{text}</span>
        </div>
      ),
      sorter: (a, b) => a.Ticker.localeCompare(b.Ticker),
      sortDirections: ['ascend', 'descend'],
      ...getColumnSearchProps('Ticker'),
      width: windowWidth < 420 ? 50 : undefined,  // Ticker column
    },
    {
      title: 'Buy Date',
      dataIndex: 'BuyDate',
      key: 'BuyDate',
      sorter: (a, b) => {
        const dateA = parseDate(a.BuyDate);
        const dateB = parseDate(b.BuyDate);
        return dateA - dateB;
      },
      sortDirections: ['ascend', 'descend'],
      ...getColumnSearchProps('BuyDate'),
      responsive: ['md'],
      width: windowWidth < 420 ? 50 : undefined,  // Buy Price column
    },
    {
      title: 'Buy Price',
      dataIndex: 'BuyPrice',
      key: 'BuyPrice',
      sorter: (a, b) => {
        const priceA = parseFloat(a.BuyPrice) || 0;
        const priceB = parseFloat(b.BuyPrice) || 0;
        return priceA - priceB;
      },
      sortDirections: ['ascend', 'descend'],
      render: (text) => {
        const price = parseFloat(text);
        if (isNaN(price)) return '$0';
        return windowWidth < 420 ? `$${price.toFixed(0)}` : `$${price.toFixed(2)}`;
      },
      width: windowWidth < 420 ? 40 : undefined,  // Buy Price column
    },
    {
      title: 'Sell Date',
      dataIndex: 'SellDate',
      key: 'SellDate',
      sorter: (a, b) => {
        if (!a.SellDate) return 1;
        if (!b.SellDate) return -1;
        const dateA = parseDate(a.SellDate);
        const dateB = parseDate(b.SellDate);
        return dateA - dateB;
      },
      sortDirections: ['ascend', 'descend'],
      ...getColumnSearchProps('SellDate'),
      render: (text) => text || '-',
      responsive: ['lg'],
      width: windowWidth < 420 ? 50 : undefined,  // Current/Sell Price column
    },
    {
      title: windowWidth < 420 ? '$' : 'Current/Sell Price',
      dataIndex: 'CurrentSellPrice',
      key: 'CurrentSellPrice',
      sorter: (a, b) => {
        const priceA = parseFloat(a.CurrentSellPrice) || 0;
        const priceB = parseFloat(b.CurrentSellPrice) || 0;
        return priceA - priceB;
      },
      sortDirections: ['ascend', 'descend'],
      render: (text) => {
        const price = parseFloat(text);
        if (isNaN(price)) return '$0';
        return windowWidth < 420 ? `$${price.toFixed(0)}` : `$${price.toFixed(2)}`;
      },
      responsive: ['sm'],
      width: windowWidth < 420 ? 50 : undefined,  // Current/Sell Price column
    },
    {
      title: 'Return',
      key: 'Return',
      render: (_, record) => {
        // First try to use the return value from CSV if available
        if (record.ReturnValue !== null && !isNaN(record.ReturnValue)) {
          const color = record.ReturnValue >= 0 ? 'green' : 'red';
          return <Text style={{ color, fontSize: windowWidth <420 ? '9px' : undefined }}>{record.ReturnValue}%</Text>;
        }
        
        // Otherwise calculate it from buy and current/sell prices
        const buyPrice = parseFloat(record.BuyPrice) || 0;
        const currentSellPrice = parseFloat(record.CurrentSellPrice) || 0;
        
        // Avoid division by zero
        if (buyPrice === 0) {
          return <Text style={{ fontSize: windowWidth < 420 ? '9px' : undefined }}>N/A</Text>;
        }
        
        const returnValue = ((currentSellPrice - buyPrice) / buyPrice) * 100;
        
        // Check if return is a valid number
        if (isNaN(returnValue)) {
          return <Text style={{ fontSize: windowWidth < 420 ? '9px' : undefined }}>N/A</Text>;
        }
        
        const color = returnValue >= 0 ? 'green' : 'red';
        return <Text style={{ color, fontSize: windowWidth < 420 ? '9px' : undefined }}>{returnValue}%</Text>;
      },
      sorter: (a, b) => {
        // First use ReturnValue from CSV if available
        if (a.ReturnValue !== null && !isNaN(a.ReturnValue) && 
            b.ReturnValue !== null && !isNaN(b.ReturnValue)) {
          return a.ReturnValue - b.ReturnValue;
        }
        
        // Otherwise calculate from prices
        const buyPriceA = parseFloat(a.BuyPrice) || 0;
        const currentSellPriceA = parseFloat(a.CurrentSellPrice) || 0;
        const buyPriceB = parseFloat(b.BuyPrice) || 0;
        const currentSellPriceB = parseFloat(b.CurrentSellPrice) || 0;
        
        // Avoid division by zero
        if (buyPriceA === 0 || buyPriceB === 0) {
          return 0;
        }
        
        const returnA = ((currentSellPriceA - buyPriceA) / buyPriceA) * 100;
        const returnB = ((currentSellPriceB - buyPriceB) / buyPriceB) * 100;
        
        // Handle NaN values in sort
        if (isNaN(returnA) && isNaN(returnB)) return 0;
        if (isNaN(returnA)) return 1;
        if (isNaN(returnB)) return -1;
        
        return returnA - returnB;
      },
      sortDirections: ['ascend', 'descend'],
      width: windowWidth < 420 ? 50 : undefined,  // Return column
    },
  ];

  /**
   * Check if a ticker exists in the CSV data
   * Uses caching for efficiency when checking multiple tickers
   */
  const checkTickerExists = (ticker, callback) => {
    if (!ticker) {
      callback(false);
      return;
    }

    // Case insensitive comparison
    const tickerLower = ticker.toLowerCase();
    
    // Use cached headers array if available to avoid unnecessary fetches
    if (window.csvHeaders) {
      const exists = window.csvHeaders.some(header => header.toLowerCase() === tickerLower);
      callback(exists);
      return;
    }
    
    fetch('/data/return_over_time.csv')
      .then(response => response.text())
      .then(csvText => {
        const headers = csvText.split('\n')[0].split(',').map(h => h.trim());
        // Cache the headers for future use
        window.csvHeaders = headers;
        const exists = headers.some(header => header.toLowerCase() === tickerLower);
        callback(exists);
      })
      .catch(error => {
        console.error('Error checking ticker existence:', error);
        callback(false);
      });
  };
  
  /**
   * Handle row click in the transactions table
   * Selects the row and loads data for the corresponding ticker
   */
  const handleRowClick = (record) => {
    // If the row is already selected, deselect it
    if (selectedRowKey === record.key) {
      setSelectedRowKey(null);
      setSelectedTicker(null);
      setTickerChartData([]);
      resetMemoization();
      
      return;
    }
    
    setSelectedRowKey(record.key);
    resetMemoization();
    
    // Check if the ticker exists in return_over_time.csv
    checkTickerExists(record.Ticker, (exists) => {
      if (exists) {
        // Load data for the selected ticker
        loadTickerData(record.Ticker);
      } else {
        setTickerChartData([]);
        setSelectedTicker(null);
      }
    });
  };

  /**
   * Load initial data on component mount
   * Fetches transaction and chart data from CSV files
   */
  useEffect(() => {
    // Fetch table data
    fetch('/data/current_holdings.csv')
    .then(response => response.text())
    .then(csvText => {
      const parsed = Papa.parse(csvText, { header: true });
      const parsedData = parsed.data;
      
      // Filter out rows without the required fields
      const tableData = parsedData
        .filter(row => {
          // Skip empty objects or rows that don't have any data
          if (!row || Object.keys(row).length === 0) return false;
          
          // Check for ticker
          const hasTicker = row.Ticker || row.ticker || row.TICKER;
          
          // Check for buy date
          const hasBuyDate = row.BuyDate || row['Buy Date'] || row.buydate || row.BUYDATE || row['buy date'];
          
          // Check for buy price
          const hasBuyPrice = row.BuyPrice || row['Buy Price'] || row.buyprice || row.BUYPRICE || row['buy price'];
          
          return hasTicker && hasBuyDate && hasBuyPrice;
        })
        .map((row, index) => {
          // Get ticker value
          const ticker = row.Ticker || row.ticker || row.TICKER;
          
          // Get buy date
          const buyDate = row.BuyDate || row['Buy Date'] || row.buydate || row.BUYDATE || row['buy date'];
          
          // Get buy price - handle string values with $ or commas
          const buyPriceStr = row.BuyPrice || row['Buy Price'] || row.buyprice || row.BUYPRICE || row['buy price'];
          const cleanBuyPriceStr = String(buyPriceStr).replace(/[$,]/g, '');
          const buyPrice = parseFloat(cleanBuyPriceStr) || 0;
          
          // Handle sell date - match the exact column name from CSV
          const sellDate = row['sell date'] || row.SellDate || row['Sell Date'] || row.selldate || row.SELLDATE || '';
          
          // Get current/sell price - IMPORTANT: Match the exact column name "Current/Sell Price" from CSV
          let currentSellPrice = 0;
          const currentSellPriceStr = row['Current/Sell Price'] || row.SellPrice || row['Sell Price'] || row.CurrentPrice || row['Current Price'];
          
          if (currentSellPriceStr) {
            const cleanCurrentSellPriceStr = String(currentSellPriceStr).replace(/[$,%]/g, '');
            currentSellPrice = parseFloat(cleanCurrentSellPriceStr) || 0;
          }
          
          // Get return value directly from CSV if available
          let returnValue = null;
          if (row.Return) {
            // Clean up the return value (remove % and handle formatting)
            const cleanReturnStr = String(row.Return).replace(/%/g, '');
            returnValue = parseFloat(cleanReturnStr);
          }
          
          return {
            key: index.toString(),
            Ticker: ticker,
            BuyDate: buyDate,
            BuyPrice: buyPrice,
            SellDate: sellDate,
            CurrentSellPrice: currentSellPrice,
            ReturnValue: returnValue // Store the return value from CSV
          };
        });
      
      if (tableData.length === 0) {
        console.warn('No table data found after processing CSV. Make sure column names match the expected format.');
      }
      
      setTableData(tableData);
    })
    .catch(error => {
      console.error('Error loading current holdings data:', error);
      setTableData([]);
    });

    // Updated chart data processing for Recharts format
    fetch('/data/return_over_time.csv')
      .then(response => response.text())
      .then(csvText => {
        const parsedData = Papa.parse(csvText, { header: true }).data;
        
        // Transform data for Recharts format (flat structure)
        const formattedData = parsedData
          .filter(row => row.Date) // Filter out rows without a date
          .map(row => ({
            date: row.Date,
            formattedDate: formatDateForAxis(row.Date), // Add formatted date for display
            portfolio: row['Cogitat'] !== undefined && row['Cogitat'] !== '' ? parseFloat(row['Cogitat']) : null,
            sp500: row.SP500 !== undefined && row.SP500 !== '' ? parseFloat(row.SP500) : null,
            isSaturday: isSaturdayDate(row.Date) // Add flag for Saturdays
          }));
        
        // Sort the data by date
        formattedData.sort((a, b) => {
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          return dateA - dateB;
        });
        
        setChartData(formattedData);
        // Apply the 1M filter to the initial data
        const filteredData = filterDataByTime(formattedData, 'all');
        setFilteredChartData(filteredData);
      })
      .catch(error => console.error('Error loading chart data:', error));
  }, []);

  // Add resize event listener for responsive handling
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Combine chart data with ticker data
   * Uses memoization for better performance
   */
  const getCombinedChartData = () => {
    // Create a key based on the current state that affects the chart data
    const newKey = `${timeFilter}-${selectedTicker}-${filteredChartData.length}-${tickerChartData.length}`;
    
    // If we have memoized data and the key hasn't changed, return the memoized data
    if (memoizedChartData && newKey === memoizationKey) {
      return memoizedChartData;
    }
    
    // Otherwise, calculate the combined data
    let combinedData;
    
    if (!selectedTicker || tickerChartData.length === 0) {
      combinedData = filteredChartData;
    } else {
      // Create a map of dates for faster lookup
      const dateMap = new Map();
      
      // First, add all dates from the filtered chart data
      filteredChartData.forEach(item => {
        dateMap.set(item.date, { ...item });
      });
      
      // Then, merge ticker data with existing chart data, but only for dates that exist in the filtered data
      tickerChartData.forEach(tickerItem => {
        if (dateMap.has(tickerItem.date)) {
          const existingItem = dateMap.get(tickerItem.date);
          existingItem.tickerValue = tickerItem.tickerValue;
        }
      });
      
      // Convert map back to array and sort by date
      combinedData = Array.from(dateMap.values());
      combinedData.sort((a, b) => {
        try {
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          return dateA - dateB;
        } catch (e) {
          return 0;
        }
      });
    }
    
    // Memoize the result
    setMemoizedChartData(combinedData);
    setMemoizationKey(newKey);
    
    return combinedData;
  };

  /**
   * Render card-based transaction list for mobile devices
   * Provides a more compact and mobile-friendly view of transactions
   */
  const renderMobileTransactionsList = () => {
    return (
      <div className="mobile-transactions-list">
        {tableData.map((record) => {
          // Calculate return value if not available in CSV
          let returnValue = record.ReturnValue;
          if (returnValue === null || isNaN(returnValue)) {
            const buyPrice = parseFloat(record.BuyPrice) || 0;
            const currentSellPrice = parseFloat(record.CurrentSellPrice) || 0;
            if (buyPrice > 0) {
              returnValue = ((currentSellPrice - buyPrice) / buyPrice) * 100;
              if (isNaN(returnValue)) returnValue = 0;
            } else {
              returnValue = 0;
            }
          }

          const isSelected = record.key === selectedRowKey;
          // Determine if the ticker has been sold (has a sell date)
          const hasSold = record.SellDate && record.SellDate.trim() !== '';
          
          return (
            <div 
              key={record.key}
              className={`transaction-card ${isSelected ? 'selected-card' : ''}`}
              onClick={() => handleRowClick(record)}
              style={{
                padding: '12px',
                marginBottom: '10px',
                border: '1px solid #eaeaea',
                borderRadius: '8px',
                backgroundColor: isSelected ? '#e6f7ff' : 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                position: 'relative',
                transition: 'background-color 0.3s'
              }}
            >
              {/* Ticker and Return value in the first row */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isSelected && (
                    <CheckCircleFilled 
                      style={{ 
                        color: '#1890ff', 
                        marginRight: '6px',
                        fontSize: '14px' 
                      }} 
                    />
                  )}
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{record.Ticker}</span>
                </div>
                <span style={{ 
                  color: returnValue >= 0 ? 'green' : 'red',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}>
                  {returnValue}%
                </span>
              </div>
              
              {/* Price and date information in two columns - Buy on left, Current/Sell on right */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                {/* Left side - Buy data */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#666' }}>Buy: </span>
                    <span style={{ fontSize: '14px' }}>${record.BuyPrice}</span>
                  </div>
                  
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                    {record.BuyDate}
                  </div>
                </div>
                
                {/* Right side - Current/Sell data */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#666' }}>{hasSold ? 'Sold:' : 'Current:'} </span>
                    <span style={{ fontSize: '14px' }}>${record.CurrentSellPrice}</span>
                  </div>
                  
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                    {record.SellDate || '----'}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Render time filter buttons
   * Allows selection of different time ranges
   */
  const renderTimeFilters = () => {
    const timeOptions = [
      { value: '1m', label: '1M' },
      { value: '3m', label: '3M' },
      { value: '6m', label: '6M' },
      { value: '1y', label: '1Y' },
      { value: 'all', label: 'Max' }
    ];
  
    const isMobile = windowWidth < 480;
    
    return (
      <div className="time-filter-buttons" style={{
        display: 'flex',
        justifyContent: 'center',  // Center buttons horizontally
        flexWrap: 'wrap',          // Allow wrapping on smaller screens
        gap: '2px'                 // Space between buttons
      }}>
        <Space size={isMobile ? "small" : "small"} wrap>
          {timeOptions.map(option => (
            <Button
              key={option.value}
              type={timeFilter === option.value ? 'primary' : 'default'}
              onClick={() => handleTimeFilterChange(option.value)}
              size={isMobile ? "middle" : "large"}
              style={{
                padding: isMobile ? '0 8px' : undefined,
                fontSize: isMobile ? '12px' : undefined,
                fontWeight: 'normal'
              }}
              className={timeFilter === option.value ? 'active-filter' : ''}
            >
              {option.label}
            </Button>
          ))}
        </Space>
      </div>
    );
  };

  // Get the combined chart data once per render
  const combinedChartData = getCombinedChartData();

  // Component lifecycle management
  useEffect(() => {
    
    
    return () => {
      
    };
  }, []);

  
  useEffect(() => {
    // Data loading effect
  }, [tableData]);

  useEffect(() => {
    // Handle filtered data changes
  }, [filteredChartData, timeFilter]);

  // Add this conditional style for exactly 400px width
  const tableStyle = {
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.01)",
    border: "1px solid rgba(0, 0, 0, 0.01)",
    ...(windowWidth === 420 ? {
      transform: 'scale(0.98)',
      transformOrigin: 'top left',
      margin: '0 auto'
    } : {})
  };

  return (
    <div className="vault-content">
      <h3
        style={{
          fontSize: windowWidth < 480 ? "20px" : "24px",
          textAlign: "center",
          marginBottom: windowWidth < 480 ? "15px" : "20px",
          padding: "0 10px",
        }}
      >
        Return Over Time
      </h3>

      {/* Performance chart section */}
      <div
        className="chart-container"
        style={{
          maxWidth: "100%",
          margin: "0 auto 20px auto",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
          borderRadius: "8px",
          padding: windowWidth < 420 ? "5px 2.5px" : "15px 10px",
          overflowX: "hidden",
        }}
      >
        {isTickerDataLoading ? (
          <div className="loading-container">Loading ticker data...</div>
        ) : (
          <>
            {/* Chart Legend */}
            <div
              className="chart-legend"
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "0 0 20px 0",
                flexWrap: "nowrap", // Change from 'wrap' to 'nowrap' to force single row
                gap: windowWidth < 420 ? "2px" : "10px", // Reduce gap further on mobile
                width: "100%", // Ensure full width
                overflow: "hidden", // Prevent horizontal scroll
                padding: "0 2px", //Adds smal paddding on the sides
                boxSizing: "border-box", //Ensure pedding dont cause
                minWidth: "0", // Ensure content can shrink below natural widthq
              }}
            >
              <div
                className="legend-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: windowWidth < 420 ? "0 1 auto" : "0 0 auto", // Allow shrinking on mobile
                  minWidth: windowWidth < 420 ? "0" : "auto", // Allow content to shrink below natural width
                }}
              >
                <div
                  style={{
                    width: windowWidth < 420 ? "10px" : "20px",
                    height: windowWidth < 420 ? "10px" : "20px",
                    backgroundColor: seriesColors["Cogitat"],
                    marginRight: windowWidth < 420 ? "2px" : "8px",
                    borderRadius: "3px",
                  }}
                ></div>
                <span
                  style={{
                    fontSize: windowWidth < 420 ? "10px" : "16px",
                    whiteSpace: "nowrap", // Prevent text wrapping
                  }}
                >
                  Cogitat
                </span>
              </div>
              <div
                className="legend-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: windowWidth < 420 ? "0 1 auto" : "0 0 auto",
                  minWidth: windowWidth < 420 ? "0" : "auto",
                }}
              >
                <div
                  style={{
                    width: windowWidth < 420 ? "10px" : "20px",
                    height: windowWidth < 420 ? "10px" : "20px",
                    backgroundColor: seriesColors["S&P 500"],
                    marginRight: windowWidth < 420 ? "2px" : "8px",
                    borderRadius: "3px",
                  }}
                ></div>
                <span
                  style={{
                    fontSize: windowWidth < 420 ? "10px" : "16px",
                    whiteSpace: "nowrap",
                  }}
                >
                  S&P 500
                </span>
              </div>
              {selectedTicker && tickerChartData.length > 0 && (
                <div
                  className="legend-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: windowWidth < 420 ? "0 1 auto" : "0 0 auto",
                    minWidth: windowWidth < 420 ? "0" : "auto",
                  }}
                >
                  <div
                    style={{
                      width: windowWidth < 420 ? "10px" : "20px",
                      height: windowWidth < 420 ? "10px" : "20px",
                      backgroundColor: seriesColors["Selected Ticker"],
                      marginRight: windowWidth < 420 ? "2px" : "8px",
                      borderRadius: "3px",
                    }}
                  ></div>
                  <span
                    style={{
                      fontSize: windowWidth < 420 ? "10px" : "16px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: windowWidth < 420 ? "60px" : "none", // Limit ticker text width on mobile
                    }}
                  >
                    {selectedTicker}
                  </span>
                </div>
              )}
            </div>
            <ResponsiveContainer
              width={windowWidth < 400 ? 320 : windowWidth < 768 ? 380 : "100%"}
              height={windowWidth < 400 ? 220 : windowWidth < 768 ? 280 : 400}
              debounce={50}
            >
              {" "}
              <LineChart
                data={combinedChartData}
                margin={
                  windowWidth < 420
                    ? { top: 5, right: 10, left: 0, bottom: 40 }
                    : { top: 5, right: 20, left: 20, bottom: 40 }
                }
                throttleDelay={50}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E2E8F0"
                  vertical={false}
                />
                <XAxis
                  dataKey="formattedDate"
                  angle={0}
                  textAnchor="middle"
                  tick={{
                    fontSize: windowWidth < 480 ? 10 : 14,
                    fill: "#64748B",
                  }}
                  tickMargin={10}
                  height={30}
                  strokeWidth={2} // makes the X-axis thicker (default is 1)
                  ticks={
                    combinedChartData.length > 8
                      ? Array.from(
                          { length: windowWidth < 480 ? 4 : 8 },
                          (_, i) =>
                            Math.floor(
                              (i * (combinedChartData.length - 1)) /
                                (windowWidth < 480 ? 3 : 7)
                            )
                        )
                          .map(
                            (index) => combinedChartData[index]?.formattedDate
                          )
                          .filter(Boolean)
                      : undefined
                  }
                  stroke="#64748B"
                />
                <YAxis
                  type="number"
                  domain={["auto", "auto"]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{
                    fontSize: windowWidth < 480 ? 10 : 14,
                    fill: "#64748B",
                  }}
                  stroke="#64748B"
                  width={windowWidth < 420 ? 45 : 50}
                  strokeWidth={2} // makes the X-axis thicker (default is 1)
                />
                <text
                  x={windowWidth < 480 ? -100 : -130}
                  y={20}
                  transform="rotate(-90)"
                  textAnchor="middle"
                  fill="#64748B"
                  fontSize={windowWidth < 480 ? 12 : 14}
                  style={{ display: windowWidth < 400 ? "none" : "block" }}
                >
                  Return (%)
                </text>
                <RechartsTooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ zIndex: 1000 }}
                  cursor={{ strokeDasharray: "3 3", stroke: "#94A3B8" }}
                  isAnimationActive={false}
                />

                {/* Reference lines for Saturdays */}
                {combinedChartData.map((entry, index) =>
                  entry.isSaturday ? (
                    <ReferenceLine
                      key={`ref-${index}`}
                      x={entry.formattedDate}
                      stroke="#CBD5E1"
                      strokeDasharray="3 3"
                      strokeWidth={1}
                      ifOverflow="extendDomain"
                    />
                  ) : null
                )}

                {/* Portfolio performance line */}
                <Line
                  type="monotone"
                  dataKey="portfolio"
                  stroke={seriesColors["Cogitat"]}
                  name="Cogitat"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  activeDot={{ r: 3 }}
                />

                {/* S&P 500 comparison line */}
                <Line
                  type="monotone"
                  dataKey="sp500"
                  stroke={seriesColors["S&P 500"]}
                  name="S&P 500"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  activeDot={{ r: 3 }}
                />

                {/* Selected ticker line (if available) */}
                {selectedTicker && tickerChartData.length > 0 && (
                  <Line
                    type="monotone"
                    dataKey="tickerValue"
                    stroke={seriesColors["Selected Ticker"]}
                    name={selectedTicker}
                    strokeWidth={2}
                    dot={false}
                    connectNulls={true}
                    isAnimationActive={false}
                    activeDot={{ r: 3 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>

            {/* Timeline filters now inside chart container */}
            <div
              className="time-filters-container"
              style={{
                marginTop: "0px", // Increase top margin
                marginBottom: "0px", // Add bottom margin
                textAlign: "center",
                padding: "1px", // Add padding around the content
                maxWidth: "100%", // Limit width to percentage of parent
                margin: "1px auto 0", // Center with auto left/right margins
              }}
            >
              {renderTimeFilters()}
            </div>
          </>
        )}
      </div>

      {/* Transactions Table section */}
      <h2
        className="transactions-title"
        style={{
          fontSize:
            windowWidth < 420 ? "16px" : windowWidth < 480 ? "20px" : "24px",
          marginTop:
            windowWidth < 420 ? "10px" : windowWidth < 480 ? "15px" : "20px",
          marginBottom: windowWidth < 420 ? "5px" : "10px",
        }}
      >
        Transactions
      </h2>
      {windowWidth < 480 ? (
        renderMobileTransactionsList()
      ) : (
        <div
          className="table-container"
          style={tableStyle}
        >
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: windowWidth < 400 ? 4 : windowWidth < 768 ? 5 : 10,
              size: "small",
              simple: windowWidth < 480 ? true : false,
              showSizeChanger: false,
            }}
            size={
              windowWidth < 420
                ? "small"
                : windowWidth < 768
                ? "small"
                : "middle"
            }
            bordered={false} // Remove the border
            scroll={{
              x: windowWidth < 420 ? 360 : windowWidth < 768 ? 400 : undefined,
              y: windowWidth < 420 ? 320 : undefined,
            }}
            rowClassName={(record) =>
              record.key === selectedRowKey ? "selected-row" : ""
            }
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              onMouseEnter: (e) => {
                if (record.key !== selectedRowKey) {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }
              },
              onMouseLeave: (e) => {
                if (record.key !== selectedRowKey) {
                  e.currentTarget.style.backgroundColor = "";
                }
              },
              style: {
                cursor: "pointer",
                backgroundColor: record.key === selectedRowKey ? "#e6f7ff" : "",
                transition: "background-color 0.3s",
                fontSize: windowWidth < 480 ? "12px" : "inherit",
              },
            })}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Main component that handles authentication and renders VaultContent
 * Checks if user is authorized before displaying portfolio data
 */
const TheVault = () => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // Check authorization on component mount
  useEffect(() => {
    const validateAuth = async () => {
      const isValid = await checkAuthToken();  // Check if user has a valid token
      setIsAuthorized(isValid);  // Update authorization state
    };
    validateAuth();
  }, []);

  // Only render content if user is authorized
  if (!isAuthorized) {
    return null;  // Return nothing if not authorized
  }
  
  return (
    <div className="vault-container">
      <VaultContent />
    </div>
  );
};

export default TheVault;